import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { formatGP, STATUS_COLOR } from "@/lib/format";
import { PageHead } from "@/pages/admin/AdminLayout";

const STATUSES = ["pending", "paid", "delivering", "delivered", "cancelled"];

export default function Orders() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const { data: orders = [] } = useQuery({ queryKey: ["admin-orders", status], queryFn: () => api.get("/admin/orders", { params: { status } }).then((r) => r.data), refetchInterval: 10000 });

  const setSt = async (o, st) => {
    try { await api.patch(`/admin/orders/${o.id}`, { status: st }); toast.success(`${o.code} -> ${st}`); qc.invalidateQueries({ queryKey: ["admin-orders"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); qc.invalidateQueries({ queryKey: ["admin-products"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-orders">
      <PageHead title="Orders" sub="// confirm payment to release the secure RSN transfer pack instantly">
        <select className="field !w-48" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status-filter"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
      </PageHead>
      <div className="border border-[#2a2a2a] overflow-auto">
        <table className="tac-table w-full">
          <thead><tr><th>Code</th><th>Items</th><th>Total</th><th>Contact</th><th>Pay</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={7} className="text-center text-[#666] py-10" data-testid="orders-empty">No orders yet</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.code}`}>
                <td className="font-bold cursor-pointer text-white" onClick={() => setOpen(open === o.id ? null : o.id)}>{o.code}</td>
                <td>{o.items.map((i) => i.title).join(", ")}</td>
                <td className="text-[#ff2a55] font-bold">{formatGP(o.total)}</td>
                <td className="text-[#aaa]">{o.contact}<br /><span className="text-[#666] text-xs">{o.email}</span></td>
                <td className="uppercase text-xs">{o.payment_method}</td>
                <td><span className="uppercase text-xs font-bold" style={{ color: STATUS_COLOR[o.status] }} data-testid={`order-status-${o.code}`}>{o.status}</span></td>
                <td className="whitespace-nowrap">
                  {o.status === "pending" && <button className="text-xs px-2 py-1 border border-[#35d07f] text-[#35d07f] hover:bg-[#35d07f]/15 transition-colors mr-2" onClick={() => setSt(o, "paid")} data-testid={`order-paid-${o.code}`}>Confirm payment &amp; deliver</button>}
                  {o.status !== "cancelled" && o.status !== "delivered" && <button className="text-xs px-2 py-1 border border-[#ff2a55] text-[#ff2a55] hover:bg-[#ff2a55]/15 transition-colors" onClick={() => setSt(o, "cancelled")} data-testid={`order-cancel-${o.code}`}>Cancel</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && (() => { const o = orders.find((x) => x.id === open); return o ? (
        <div className="mt-4 border border-[#ff2a55]/40 bg-[#0a0a0a] p-4 text-sm" data-testid="order-detail">
          <div className="overline-label mb-2">{o.code} / history</div>
          {o.history.map((h, i) => <div key={i} className="text-[#aaa]">{h.at.slice(0, 16).replace("T", " ")} - <b className="text-white">{h.status}</b> {h.note}</div>)}
          {o.rsn && <div className="mt-2">In-game name: {o.rsn}</div>}{o.notes && <div>Notes: {o.notes}</div>}
        </div>) : null; })()}
    </div>
  );
}
